/**
 * Scrapers/Diagnose — the testing tool.
 *
 * Layout: left rail = form, right rail = result panel + history.
 * Form lets the user pick one engine (Run) or several engines
 * (Compare). Result panel renders the canonical ProbeResult
 * envelope from the backend; compare mode renders one column per
 * engine side-by-side.
 *
 * Browser-engine artefacts (rendered HTML, screenshot) live in
 * GridFS and stream through /v1/observe/probe-engines/artefact/:id.
 * The route is x-api-key gated, so we fetch the bytes via JS,
 * make a blob URL, and assign that to <iframe srcDoc>/<img src>.
 *
 * Saved recipes (form-state snapshots) live in localStorage under
 * `otr.diagnose.recipes` so a user can re-test the same target
 * later with one click.
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Image as ImageIcon,
  Loader2, Play, Plus, Save, Trash2, X,
} from 'lucide-react';
import {
  fetchArtefact,
  getEngines,
  getRun,
  listHistory,
  runCompare,
  runProbe,
  type BrowserOpts,
  type EngineCapability,
  type EngineKind,
  type HeaderPair,
  type ProbeRequest,
  type ProbeResult,
  type ProbeRunSummary,
} from '@/api/probe-engines';
import { cn } from '@/lib/cn';

const RECIPES_KEY = 'otr.diagnose.recipes';

interface FormState {
  url:        string;
  method:     ProbeRequest['method'];
  headers:    HeaderPair[];
  body:       string;
  proxy:      string;
  timeoutMs:  number | '';
  engine:     EngineKind;          // single-engine mode
  engines:    EngineKind[];        // compare mode
  compareMode:boolean;
  browser:    BrowserOpts;
}

const INITIAL_STATE: FormState = {
  url:        '',
  method:     'GET',
  headers:    [{ name: '', value: '' }],
  body:       '',
  proxy:      '',
  timeoutMs:  '',
  engine:     'direct_http',
  engines:    ['direct_http', 'docker_http'],
  compareMode:false,
  browser:    { screenshot: true, useHumanBehavior: true, humanevasionTier: 'cdp' },
};

export default function DiagnoseTab() {
  const [form, setForm] = useState<FormState>(INITIAL_STATE);
  const [activeRun, setActiveRun] = useState<ProbeResult | ProbeResult[] | null>(null);
  const qc = useQueryClient();

  const enginesQ = useQuery({
    queryKey: ['probe-engines', 'engines'],
    queryFn:  getEngines,
    staleTime: 5 * 60_000,
  });

  const historyQ = useQuery({
    queryKey: ['probe-engines', 'history'],
    queryFn:  () => listHistory({ limit: 50 }),
    refetchInterval: 30_000,
  });

  // Wider response type: single-engine returns { result }, compare
  // returns { results, groupId }. We dispatch on `'result' in data`
  // in onSuccess. The explicit union here is what TS needs to stop
  // collapsing to whichever branch it sees first.
  type RunResponse =
    | { ok: true; result: ProbeResult }
    | { ok: true; groupId: string; results: ProbeResult[] };

  const runM = useMutation<RunResponse, Error, void>({
    mutationFn: async (): Promise<RunResponse> => {
      if (!form.url) throw new Error('URL is required');
      const base: ProbeRequest = {
        url:       form.url,
        method:    form.method,
        headers:   compactHeaders(form.headers),
        body:      form.body || undefined,
        proxy:     form.proxy || undefined,
        timeoutMs: typeof form.timeoutMs === 'number' ? form.timeoutMs : undefined,
        engine:    form.engine,
        browser:   browserSubmittable(form.browser, form.engine, form.engines, form.compareMode),
      };
      if (form.compareMode) {
        if (form.engines.length === 0) throw new Error('Pick at least one engine for compare mode');
        return runCompare({ ...base, engines: form.engines });
      }
      return runProbe(base);
    },
    onSuccess: (data) => {
      if ('result' in data) setActiveRun(data.result);
      else                  setActiveRun(data.results);
      qc.invalidateQueries({ queryKey: ['probe-engines', 'history'] });
    },
  });

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[420px_1fr] gap-4">
      <BrewForm
        form={form}
        setForm={setForm}
        engines={enginesQ.data?.engines}
        onRun={() => runM.mutate()}
        running={runM.isPending}
      />
      <div className="space-y-4 min-w-0">
        {runM.error && (
          <div className="border border-red-500/30 bg-red-500/10 text-red-200 text-sm rounded p-3">
            {(runM.error as Error).message}
          </div>
        )}
        {activeRun
          ? Array.isArray(activeRun)
            ? <CompareResults results={activeRun} />
            : <SingleResult result={activeRun} />
          : <EmptyState />}
        <HistoryRail
          rows={historyQ.data?.runs ?? []}
          loading={historyQ.isLoading}
          onPick={async (id) => {
            const r = await getRun(id);
            setActiveRun(r.run);
          }}
        />
      </div>
    </div>
  );
}

// ── Brew form ──────────────────────────────────────────────────────

function BrewForm({
  form, setForm, engines, onRun, running,
}: {
  form: FormState;
  setForm: (f: FormState) => void;
  engines: Record<EngineKind, EngineCapability> | undefined;
  onRun: () => void;
  running: boolean;
}) {
  const update = <K extends keyof FormState>(k: K, v: FormState[K]) => setForm({ ...form, [k]: v });
  const updateBrowser = <K extends keyof BrowserOpts>(k: K, v: BrowserOpts[K]) =>
    setForm({ ...form, browser: { ...form.browser, [k]: v } });

  // Show browser options only when ANY selected engine supports browser.
  const showBrowserOpts = useMemo(() => {
    if (!engines) return false;
    const selected = form.compareMode ? form.engines : [form.engine];
    return selected.some((e) => engines[e]?.supportsBrowser);
  }, [engines, form.compareMode, form.engine, form.engines]);

  return (
    <section className="bg-slate-900/40 border border-slate-800 rounded-lg p-4 space-y-3 self-start">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-sm font-semibold">Brew</h2>
        <RecipeMenu form={form} setForm={setForm} />
      </div>

      <Field label="URL">
        <input
          type="url"
          value={form.url}
          placeholder="https://example.com/sch/i.html?_nkw=iphone+15"
          onChange={(e) => update('url', e.target.value)}
          className="input w-full"
          autoComplete="off"
          spellCheck={false}
        />
      </Field>

      {/* Engine select — single-pick or multi-pick depending on compareMode */}
      <Field
        label={
          <span className="flex items-center gap-2">
            Engine
            <label className="text-[11px] font-normal text-slate-400 flex items-center gap-1 cursor-pointer">
              <input
                type="checkbox"
                checked={form.compareMode}
                onChange={(e) => update('compareMode', e.target.checked)}
                className="accent-emerald-400"
              />
              compare mode
            </label>
          </span>
        }
      >
        <EnginePicker
          engines={engines}
          value={form.compareMode ? form.engines : [form.engine]}
          multiple={form.compareMode}
          onChange={(next) => {
            if (form.compareMode) update('engines', next);
            else                  update('engine',  next[0] ?? 'direct_http');
          }}
        />
      </Field>

      <div className="grid grid-cols-2 gap-2">
        <Field label="Method">
          <select
            value={form.method ?? 'GET'}
            onChange={(e) => update('method', e.target.value as FormState['method'])}
            className="input w-full"
          >
            {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD'].map((m) => <option key={m}>{m}</option>)}
          </select>
        </Field>
        <Field label="Timeout (ms)">
          <input
            type="number"
            value={form.timeoutMs}
            placeholder="30000"
            onChange={(e) => update('timeoutMs', e.target.value ? Number(e.target.value) : '')}
            className="input w-full"
          />
        </Field>
      </div>

      <Collapsible label="Headers" defaultOpen>
        <HeaderEditor pairs={form.headers} onChange={(p) => update('headers', p)} />
      </Collapsible>

      <Collapsible label="Body">
        <textarea
          value={form.body}
          onChange={(e) => update('body', e.target.value)}
          placeholder='{"hello": "world"}'
          rows={4}
          className="input w-full font-mono text-xs resize-y"
        />
      </Collapsible>

      <Collapsible label="Proxy (optional)">
        <input
          type="text"
          value={form.proxy}
          placeholder="http://user:pass@host:8080"
          onChange={(e) => update('proxy', e.target.value)}
          className="input w-full font-mono text-xs"
          autoComplete="off"
        />
        <p className="text-[11px] text-slate-500 mt-1">
          Supported by direct_http and docker_http. CF engines fail-loud
          with proxy_unsupported_on_cf.
        </p>
      </Collapsible>

      {showBrowserOpts && (
        <Collapsible label="Browser options" defaultOpen>
          <div className="space-y-2">
            <label className="flex items-center gap-2 text-xs text-slate-300">
              <input
                type="checkbox"
                checked={!!form.browser.screenshot}
                onChange={(e) => updateBrowser('screenshot', e.target.checked)}
                className="accent-emerald-400"
              />
              Capture full-page screenshot
            </label>
            <Field label="Wait for selector (optional)" small>
              <input
                value={form.browser.waitForSelector ?? ''}
                onChange={(e) => updateBrowser('waitForSelector', e.target.value || undefined)}
                placeholder=".s-item, #productGrid"
                className="input w-full font-mono text-xs"
              />
            </Field>
            <Field label="humanevasion tier" small>
              <select
                value={form.browser.humanevasionTier ?? 'cdp'}
                onChange={(e) => updateBrowser('humanevasionTier', e.target.value as BrowserOpts['humanevasionTier'])}
                className="input w-full"
              >
                <option value="js">js (lightest)</option>
                <option value="cdp">cdp (default)</option>
                <option value="vm">vm (heaviest)</option>
              </select>
            </Field>
          </div>
        </Collapsible>
      )}

      <button
        type="button"
        onClick={onRun}
        disabled={running || !form.url}
        className={cn(
          'w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium rounded',
          'bg-emerald-500/20 border border-emerald-500/40 text-emerald-200 hover:bg-emerald-500/30',
          'disabled:opacity-50 disabled:cursor-not-allowed transition',
        )}
      >
        {running
          ? <><Loader2 className="w-4 h-4 animate-spin" aria-hidden /> Running…</>
          : <><Play className="w-4 h-4" aria-hidden /> {form.compareMode ? 'Compare' : 'Run'}</>}
      </button>
    </section>
  );
}

function Field({ label, small, children }: { label: React.ReactNode; small?: boolean; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <label className={cn('block text-slate-300', small ? 'text-[11px]' : 'text-xs font-medium')}>
        {label}
      </label>
      {children}
    </div>
  );
}

function Collapsible({ label, children, defaultOpen = false }: {
  label: string; children: React.ReactNode; defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-t border-slate-800 pt-2">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs font-medium text-slate-300 hover:text-white"
      >
        {open ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {label}
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
}

// ── Engine picker (single + multi) ─────────────────────────────────

function EnginePicker({
  engines, value, multiple, onChange,
}: {
  engines:  Record<EngineKind, EngineCapability> | undefined;
  value:    EngineKind[];
  multiple: boolean;
  onChange: (v: EngineKind[]) => void;
}) {
  if (!engines) return <div className="text-xs text-slate-500">Loading engines…</div>;
  const all = Object.entries(engines) as Array<[EngineKind, EngineCapability]>;

  const toggle = (k: EngineKind) => {
    if (!multiple) { onChange([k]); return; }
    onChange(value.includes(k) ? value.filter((v) => v !== k) : [...value, k]);
  };

  return (
    <div className="space-y-1.5">
      {all.map(([key, cap]) => {
        const checked = value.includes(key);
        return (
          <label
            key={key}
            className={cn(
              'flex items-start gap-2 px-2 py-1.5 rounded border text-xs cursor-pointer transition',
              !cap.available && 'opacity-50 cursor-not-allowed',
              checked
                ? 'border-emerald-500/40 bg-emerald-500/10'
                : 'border-slate-800 bg-slate-900/40 hover:border-slate-700',
            )}
            title={cap.description}
          >
            <input
              type={multiple ? 'checkbox' : 'radio'}
              name="engine"
              checked={checked}
              disabled={!cap.available}
              onChange={() => toggle(key)}
              className="mt-0.5 accent-emerald-400"
            />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5 flex-wrap">
                <code className="text-slate-100">{key}</code>
                {cap.supportsProxy   && <span className="text-[10px] text-emerald-300/80">+proxy</span>}
                {cap.supportsBrowser && <span className="text-[10px] text-cyan-300/80">+browser</span>}
                {!cap.available      && <span className="text-[10px] text-amber-300/80">unavailable</span>}
              </div>
              <p className="text-[11px] text-slate-500 leading-tight">{cap.description}</p>
            </div>
          </label>
        );
      })}
    </div>
  );
}

// ── Header editor ──────────────────────────────────────────────────

function HeaderEditor({ pairs, onChange }: { pairs: HeaderPair[]; onChange: (p: HeaderPair[]) => void }) {
  const update = (i: number, p: HeaderPair) => {
    const next = pairs.slice();
    next[i] = p;
    onChange(next);
  };
  return (
    <div className="space-y-1">
      {pairs.map((p, i) => (
        <div key={i} className="flex gap-1">
          <input
            value={p.name}
            placeholder="Header"
            onChange={(e) => update(i, { ...p, name: e.target.value })}
            className="input font-mono text-xs flex-1"
          />
          <input
            value={p.value}
            placeholder="Value"
            onChange={(e) => update(i, { ...p, value: e.target.value })}
            className="input font-mono text-xs flex-1"
          />
          <button
            type="button"
            onClick={() => onChange(pairs.filter((_, j) => j !== i))}
            className="px-1.5 text-slate-500 hover:text-red-300"
            aria-label="Remove"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={() => onChange([...pairs, { name: '', value: '' }])}
        className="inline-flex items-center gap-1 text-[11px] text-slate-400 hover:text-white"
      >
        <Plus className="w-3 h-3" /> Add header
      </button>
    </div>
  );
}

// ── Saved recipes ──────────────────────────────────────────────────

interface SavedRecipe { name: string; form: FormState }

function loadRecipes(): SavedRecipe[] {
  if (typeof window === 'undefined') return [];
  try { return JSON.parse(window.localStorage.getItem(RECIPES_KEY) ?? '[]'); }
  catch { return []; }
}
function saveRecipes(recipes: SavedRecipe[]) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(RECIPES_KEY, JSON.stringify(recipes));
}

function RecipeMenu({ form, setForm }: { form: FormState; setForm: (f: FormState) => void }) {
  const [open, setOpen]       = useState(false);
  const [recipes, setRecipes] = useState<SavedRecipe[]>(() => loadRecipes());

  const save = () => {
    const name = window.prompt('Recipe name?');
    if (!name) return;
    const next = [...recipes.filter((r) => r.name !== name), { name, form }];
    setRecipes(next);
    saveRecipes(next);
  };
  const remove = (name: string) => {
    const next = recipes.filter((r) => r.name !== name);
    setRecipes(next);
    saveRecipes(next);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="text-[11px] text-slate-400 hover:text-white inline-flex items-center gap-1"
      >
        <Save className="w-3 h-3" /> Recipes
      </button>
      {open && (
        <div className="absolute right-0 top-5 z-10 w-64 bg-slate-900 border border-slate-800 rounded-md p-2 shadow-lg space-y-1 max-h-64 overflow-y-auto">
          <button
            type="button"
            onClick={() => { save(); setOpen(false); }}
            className="w-full text-left text-xs text-emerald-300 hover:text-emerald-200 px-1 py-0.5"
          >
            + Save current form
          </button>
          {recipes.length === 0 && <p className="text-[11px] text-slate-500 px-1">No recipes yet.</p>}
          {recipes.map((r) => (
            <div key={r.name} className="flex items-center gap-1 text-xs">
              <button
                type="button"
                onClick={() => { setForm(r.form); setOpen(false); }}
                className="flex-1 text-left text-slate-300 hover:text-white truncate"
                title={`URL: ${r.form.url}`}
              >
                {r.name}
              </button>
              <button
                type="button"
                onClick={() => remove(r.name)}
                className="text-slate-500 hover:text-red-300"
                aria-label={`Delete ${r.name}`}
              >
                <Trash2 className="w-3 h-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Result panels ──────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="border border-dashed border-slate-800 rounded-lg p-8 text-center text-sm text-slate-500">
      Run a probe or pick a recent run from the history below.
    </div>
  );
}

function SingleResult({ result }: { result: ProbeResult }) {
  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-3 space-y-3 min-w-0">
      <ResultHeader r={result} />
      <ResultBody r={result} />
    </section>
  );
}

function CompareResults({ results }: { results: ProbeResult[] }) {
  return (
    <section className="space-y-3">
      <div className="flex items-center gap-2 text-xs text-slate-400">
        Comparing <span className="text-white font-medium">{results.length}</span> engines
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {results.map((r) => (
          <div key={r.id} className="border border-slate-800 bg-slate-900/40 rounded-lg p-3 space-y-2 min-w-0">
            <ResultHeader r={r} compact />
            <ResultBody r={r} compact />
          </div>
        ))}
      </div>
    </section>
  );
}

function ResultHeader({ r, compact }: { r: ProbeResult; compact?: boolean }) {
  const verdictBadge = {
    ok:         { cls: 'bg-emerald-500/20 text-emerald-200 border-emerald-500/40', label: 'OK' },
    soft_block: { cls: 'bg-amber-500/20  text-amber-200  border-amber-500/40',  label: 'Soft block' },
    hard_block: { cls: 'bg-red-500/20    text-red-200    border-red-500/40',    label: 'Hard block' },
  }[r.blockVerdict];
  return (
    <div className="flex items-start justify-between gap-2 flex-wrap">
      <div className="min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <code className={cn('text-xs font-medium', compact ? 'text-slate-200' : 'text-white')}>
            {r.engine}
          </code>
          {r.error
            ? <span className="text-xs px-1.5 py-0.5 rounded border border-red-500/40 bg-red-500/15 text-red-200">
                <AlertCircle className="w-3 h-3 inline -mt-0.5 mr-0.5" />{r.error.kind}
              </span>
            : <span className="text-xs px-1.5 py-0.5 rounded border border-slate-700 bg-slate-800 text-slate-200">
                {r.statusCode ?? '—'}
              </span>}
          <span className={cn('text-xs px-1.5 py-0.5 rounded border', verdictBadge.cls)}>
            {verdictBadge.label}
          </span>
          <span className="text-[11px] text-slate-500">{r.timing.total}ms · {humanBytes(r.bodySizeBytes)}</span>
        </div>
        {!compact && (
          <p className="text-[11px] text-slate-500 mt-1 truncate" title={r.finalUrl}>{r.finalUrl}</p>
        )}
      </div>
    </div>
  );
}

function ResultBody({ r, compact }: { r: ProbeResult; compact?: boolean }) {
  return (
    <div className="space-y-2">
      {r.error && (
        <div className="text-xs border border-red-500/30 bg-red-500/10 text-red-200 rounded p-2">
          <div className="font-medium">{r.error.kind}</div>
          <div className="text-red-300/80">{r.error.message}</div>
        </div>
      )}

      {r.blockSignals.length > 0 && (
        <div className="text-xs space-y-1">
          <div className="font-medium text-slate-300">Block signals</div>
          {r.blockSignals.map((s, i) => (
            <div key={i} className="border border-slate-800 bg-slate-900/40 rounded px-2 py-1.5">
              <div className="flex items-center gap-1.5">
                <span className={cn(
                  'text-[10px] px-1 rounded',
                  s.severity === 'hard' ? 'bg-red-500/20 text-red-200' :
                  s.severity === 'soft' ? 'bg-amber-500/20 text-amber-200' :
                                          'bg-slate-800 text-slate-300',
                )}>{s.severity}</span>
                <code className="text-slate-200">{s.id}</code>
                <span className="text-[10px] text-slate-500">in {s.matchedAt}</span>
              </div>
              {s.excerpt && <pre className="text-[10px] text-slate-500 mt-0.5 whitespace-pre-wrap break-all">{s.excerpt}</pre>}
            </div>
          ))}
        </div>
      )}

      {!compact && r.bodyPreview && (
        <Collapsible label={`Body (${humanBytes(r.bodySizeBytes)})`}>
          <pre className="text-[11px] font-mono whitespace-pre-wrap break-all max-h-64 overflow-y-auto bg-slate-950/60 border border-slate-800 rounded p-2">
            {r.bodyPreview}
          </pre>
        </Collapsible>
      )}

      {!compact && Object.keys(r.responseHeaders).length > 0 && (
        <Collapsible label={`Response headers (${Object.keys(r.responseHeaders).length})`}>
          <div className="text-[11px] font-mono space-y-0.5 max-h-48 overflow-y-auto">
            {Object.entries(r.responseHeaders).map(([k, v]) => (
              <div key={k} className="grid grid-cols-[1fr_2fr] gap-2">
                <span className="text-slate-500">{k}</span>
                <span className="text-slate-300 break-all">{v}</span>
              </div>
            ))}
          </div>
        </Collapsible>
      )}

      {r.screenshotId && <ArtefactImage id={r.screenshotId} />}
      {r.renderedHtmlId && <ArtefactHtml id={r.renderedHtmlId} />}
    </div>
  );
}

// Auth-friendly artefact streamers — fetch via JS, blob → object URL.
function ArtefactImage({ id }: { id: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let revoke: (() => void) | undefined;
    fetchArtefact(id)
      .then((a) => { setUrl(a.url); revoke = a.revoke; })
      .catch((e) => setErr(e.message));
    return () => revoke?.();
  }, [id]);
  return (
    <Collapsible label="Screenshot" defaultOpen>
      {err && <div className="text-xs text-red-300">{err}</div>}
      {!url && !err && <div className="text-xs text-slate-500 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Loading…</div>}
      {url && <img src={url} alt="probe screenshot" className="max-w-full rounded border border-slate-800" />}
    </Collapsible>
  );
}

function ArtefactHtml({ id }: { id: string }) {
  const [src, setSrc] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let revoke: (() => void) | undefined;
    fetchArtefact(id)
      .then((a) => { setSrc(a.url); revoke = a.revoke; })
      .catch((e) => setErr(e.message));
    return () => revoke?.();
  }, [id]);
  return (
    <Collapsible label="Rendered HTML">
      {err && <div className="text-xs text-red-300">{err}</div>}
      {/* Sandboxed iframe — no scripts, no same-origin, no top-nav.
          Renders the captured DOM safely as a static document. */}
      {src && (
        <iframe
          src={src}
          sandbox=""
          title="rendered HTML"
          className="w-full h-96 bg-white rounded border border-slate-800"
        />
      )}
    </Collapsible>
  );
}

// ── History rail ───────────────────────────────────────────────────

function HistoryRail({
  rows, loading, onPick,
}: { rows: ProbeRunSummary[]; loading: boolean; onPick: (id: string) => void }) {
  return (
    <section className="border border-slate-800 bg-slate-900/40 rounded-lg p-3 space-y-2">
      <h3 className="text-xs font-semibold text-slate-300">Recent runs</h3>
      {loading && <div className="text-xs text-slate-500">Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-xs text-slate-500">No history yet.</div>}
      <div className="space-y-1 max-h-72 overflow-y-auto">
        {rows.map((r) => (
          <button
            key={r.id}
            type="button"
            onClick={() => onPick(r.id)}
            className="w-full text-left flex items-center gap-2 text-xs px-2 py-1.5 rounded hover:bg-slate-800/60 transition"
          >
            <span className="text-slate-500 shrink-0 w-12 tabular-nums">{shortTime(r.ts)}</span>
            <code className="text-slate-300 shrink-0">{r.engine}</code>
            <span className={cn(
              'text-[10px] px-1 rounded shrink-0',
              r.hasError                ? 'bg-red-500/15 text-red-200' :
              r.blockVerdict === 'hard_block' ? 'bg-red-500/15 text-red-200' :
              r.blockVerdict === 'soft_block' ? 'bg-amber-500/15 text-amber-200' :
                                                'bg-emerald-500/15 text-emerald-200',
            )}>
              {r.hasError
                ? 'err'
                : (r.blockVerdict === 'ok' ? <CheckCircle2 className="w-3 h-3 inline" /> : r.blockVerdict.replace('_', ' '))}
            </span>
            <span className="text-slate-400 truncate flex-1" title={r.url}>{r.url}</span>
            <span className="text-slate-500 shrink-0 tabular-nums">{r.durationMs}ms</span>
          </button>
        ))}
      </div>
    </section>
  );
}

// ── helpers ────────────────────────────────────────────────────────

function compactHeaders(pairs: HeaderPair[]): HeaderPair[] | undefined {
  const filtered = pairs.filter((p) => p.name.trim());
  return filtered.length ? filtered : undefined;
}

function browserSubmittable(
  b: BrowserOpts, single: EngineKind, multi: EngineKind[], compare: boolean,
): BrowserOpts | undefined {
  // Only forward browser opts when at least one selected engine is a browser engine.
  // Saves bytes on the wire for plain HTTP probes.
  const selected = compare ? multi : [single];
  const anyBrowser = selected.some((e) => e === 'docker_browser' || e === 'cf_browser');
  return anyBrowser ? b : undefined;
}

function humanBytes(n: number): string {
  if (n < 1024)        return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function shortTime(iso: string): string {
  try {
    const d = new Date(iso);
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
  } catch {
    return '—';
  }
}
